import {
    TableCell,
    Box,
    Typography,
    Collapse,
    Table,
    TableHead,
    TableBody,
    TableRow,
    IconButton,
    Stack,
} from '@mui/material';
import React, { useContext, useMemo, useState } from 'react';
import { documentEntitled, HeaderContext, TableMeta } from './VirtualizedTable';
import {
    formattingDate,
    formattingSingleCalender,
    formattingSingleTime,
} from 'shared/helpers/FormatDate';
import { useSelector } from 'react-redux';
import { useMeasure } from 'react-use';

import { ManagePacketService } from 'services';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Utils } from 'shared/helpers';
import { InfoIcon, RefreshCCW } from 'shared/components/AppIcons';

const TableRowVirtuoso: React.FC<ComponentRowContent> = React.forwardRef(
    (
        {
            onFinishRevalidate,
            item: _item,
            'data-item-index': index,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            ref,
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            'data-index': key,
            ...props
        },
        _ref
    ): React.ReactElement => {
        const authState = useSelector((state: any) => state.authState);
        const TableMetaProps = useContext(TableMeta);
        const [TableCellFirstWidth, setTableCellFirstWidth] =
            useState<number>(0);
        const [TableCellSecondWidth, setTableCellSecondWidth] =
            useState<number>(0);
        const [TableCellThirdWidth, setTableCellThirdWidth] =
            useState<number>(0);
        const [TableCellFourthWidth, setTableCellFourthWidth] =
            useState<number>(0);
        const [TableCellFifthWidth, setTableCellFifthWidth] =
            useState<number>(0);
        const [, setTableCellSixthWidth] = useState<number>(0);
        const [rowRef, { width }] = useMeasure<HTMLTableRowElement>();
        const headerTheme = React.useContext(HeaderContext);
        const isDataToggleExist = React.useMemo(() => {
            if (Object.keys(TableMetaProps?.toggleData ?? {}).includes(key)) {
                const i = Object.keys(
                    TableMetaProps?.toggleData ?? {}
                ).findIndex((e) => e === key);
                if (
                    Object.values(TableMetaProps?.toggleData ?? {}).at(i) ===
                    true
                ) {
                    return true;
                }
                return false;
            }
            return false;
        }, [TableMetaProps?.toggleData, key]);
        const isDataStickyExist = React.useMemo(() => {
            if (Object.keys(TableMetaProps?.stickyData ?? {}).includes(key)) {
                const i = Object.keys(
                    TableMetaProps?.stickyData ?? {}
                ).findIndex((e) => e === key);
                if (
                    Object.values(TableMetaProps?.stickyData ?? {}).at(i) ===
                    true
                ) {
                    return true;
                }
                return false;
            }
            return false;
        }, [TableMetaProps?.stickyData, key]);
        const [isOpen, setOpened] = React.useState(!!isDataToggleExist);
        const [isSticky, setSticky] = React.useState(!!isDataStickyExist);
        React.useLayoutEffect(() => {
            if (headerTheme) {
                setTableCellFirstWidth(headerTheme.packetStatus - 9);
                setTableCellSecondWidth(headerTheme.bin);
                setTableCellThirdWidth(headerTheme.loan);
                setTableCellFourthWidth(headerTheme.SLA);
                setTableCellFifthWidth(headerTheme.actions);
                setTableCellSixthWidth(headerTheme.subactions);
            }
        }, [width]);
        React.useImperativeHandle(
            _ref,
            () => ({
                setClose: () => {
                    if (isDataToggleExist) {
                        TableMetaProps?.setToggleData((prev) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const { [key]: test, ...filteredPrev } = prev;
                            return filteredPrev;
                        });
                    }
                    setOpened(false);
                },
                setOpen: () => {
                    if (!isDataToggleExist) {
                        TableMetaProps?.setToggleData((prev) => {
                            const editPrev = { ...prev, [key]: true };
                            return editPrev;
                        });
                    }
                    setOpened(true);
                },
                isOpen: () => isOpen,
                setSticky: (val) => {
                    if (val) {
                        if (!isDataStickyExist) {
                            TableMetaProps?.setStickyData((prev) => {
                                const editPrev = {
                                    ...prev,
                                    [key]: true,
                                };
                                return editPrev;
                            });
                        }
                    } else {
                        // eslint-disable-next-line no-lonely-if
                        if (isDataStickyExist) {
                            TableMetaProps?.setStickyData((prev) => {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                const { [key]: test, ...filteredPrev } = prev;
                                return filteredPrev;
                            });
                        }
                    }
                    setSticky(val);
                },
            }),
            [TableMetaProps, isDataStickyExist, isDataToggleExist, isOpen, key]
        );

        const isHavingRequiredDocument = React.useMemo(
            () => _item.reworkComments && _item.reworkComments.length > 0,

            [_item.reworkComments]
        );
        const handleForRerunValidation = React.useCallback(() => {
            ManagePacketService.rerunValidation({
                packetId: _item.actions.uuid,
            })
                .then((response) => {
                    if (typeof onFinishRevalidate === 'function') {
                        onFinishRevalidate();
                    }
                    Utils.handleSuccessResponse(response);
                })
                .catch(Utils.handleErrorResponse);
        }, [_item.actions.uuid, onFinishRevalidate]);

        const getOperatorName = React.useCallback((operatorName: any) => {
            const lastName =
                typeof operatorName === 'object' && operatorName !== null
                    ? operatorName.last
                    : '';
            const firstName =
                typeof operatorName === 'object' && operatorName !== null
                    ? operatorName.first
                    : '';
            const nameOperator = String(firstName)
                .substring(0, 1)
                .toUpperCase();

            return typeof operatorName === 'object' && operatorName !== null
                ? `${lastName}, ${nameOperator}`
                : '-';
        }, []);

        const expectedMissingDocuments: documentEntitled[] = React.useMemo(
            () =>
                Array.from(_item.reworkComments ?? [])
                    .filter((d) => d.documentId === null)
                    .map((e) => ({
                        category: e.category,
                        placeholder: e.placeholder ?? '',
                        predefinedReWorkMessage: e.predefinedMessage,
                        statusMessage: e.comments,
                    })),
            [_item.reworkComments]
        );
        const documentsRequired: documentEntitled[] = React.useMemo(
            () =>
                Array.from(_item.reworkComments ?? [])
                    .filter((d) => d.documentId !== null)
                    .map((e) => ({
                        category: e.category,
                        placeholder: e.placeholder ?? '',
                        predefinedReWorkMessage: e.predefinedMessage,
                        statusMessage: e.comments,
                    })),
            [_item.reworkComments]
        );

        const isReasonBoxExist = useMemo(
            () =>
                !!(
                    _item.reason &&
                    ((_item.packetStatus === 'Escalated' &&
                        authState.role !== 'operator') ||
                        _item.packetStatus !== 'Escalated')
                ),
            [_item.packetStatus, _item.reason, authState.role]
        );

        return (
            <>
                <TableRow
                    {...props}
                    key={`${_item.bin} - ${index}`}
                    className={`
                        ${
                            TableMetaProps?.data[index] === true
                                ? 'hold-status'
                                : 'open-status'
                        }  ${isSticky ? 'sticky-packet' : ''} ${
                        isOpen ? 'opened' : 'closed'
                    } ${
                        _item.reason &&
                        ((_item.packetStatus === 'Escalated' &&
                            authState.role !== 'operator') ||
                            _item.packetStatus !== 'Escalated')
                            ? 'reason-box'
                            : ''
                    }`}
                >
                    {props.children}
                </TableRow>
                <TableRow
                    ref={rowRef}
                    className={`${
                        isOpen ? 'packets-row selected-packets' : 'packets-row'
                    } ${isSticky ? 'sticky-packet' : ''} ${
                        TableMetaProps?.data[index] === true
                            ? 'hold-status'
                            : 'open-status'
                    } ${isReasonBoxExist ? 'reason-box' : ''} ${
                        isReasonBoxExist === false && isOpen === false
                            ? 'packet-none'
                            : ''
                    }`}
                >
                    <TableCell className="packets" colSpan={7}>
                        {_item.reason &&
                            ((_item.packetStatus === 'Escalated' &&
                                authState.role !== 'operator') ||
                                _item.packetStatus !== 'Escalated') && (
                                <Stack
                                    className="reasonBox"
                                    direction="row"
                                    spacing={2}
                                >
                                    <Box>
                                        <InfoIcon class="reason-info-icon" />
                                    </Box>
                                    <Typography className="reasonTitle">
                                        {_item.reason}
                                    </Typography>
                                </Stack>
                            )}
                        {isHavingRequiredDocument && (
                            <Box className="entitledBox">
                                <Stack direction="column">
                                    {documentsRequired &&
                                        documentsRequired.length > 0 && (
                                            <Box>
                                                <Typography className="title">
                                                    Documents required
                                                </Typography>
                                                {documentsRequired.map((el) => (
                                                    <Box className="stackedDocuments">
                                                        <Stack
                                                            spacing={1}
                                                            direction="row"
                                                        >
                                                            <FiberManualRecordIcon fontSize="small" />
                                                            <Box>
                                                                <Typography>
                                                                    {
                                                                        el.placeholder
                                                                    }
                                                                </Typography>
                                                                {el.predefinedReWorkMessage && (
                                                                    <Stack direction="row">
                                                                        <FiberManualRecordIcon fontSize="small" />
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={
                                                                                1
                                                                            }
                                                                        >
                                                                            <Typography className="heading">
                                                                                Predefined
                                                                                Reason:
                                                                            </Typography>
                                                                            <Typography>
                                                                                {
                                                                                    el.predefinedReWorkMessage
                                                                                }
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                )}
                                                                {el.statusMessage && (
                                                                    <Stack direction="row">
                                                                        <FiberManualRecordIcon fontSize="small" />
                                                                        <Stack
                                                                            direction="row"
                                                                            spacing={
                                                                                1
                                                                            }
                                                                        >
                                                                            <Typography className="heading">
                                                                                Comments:
                                                                            </Typography>
                                                                            <Typography>
                                                                                {
                                                                                    el.statusMessage
                                                                                }
                                                                            </Typography>
                                                                        </Stack>
                                                                    </Stack>
                                                                )}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    {expectedMissingDocuments &&
                                        expectedMissingDocuments.length > 0 && (
                                            <Box>
                                                <Typography className="title">
                                                    Missing Documents
                                                </Typography>
                                                {expectedMissingDocuments.map(
                                                    (el) => (
                                                        <Box className="stackedDocuments">
                                                            <Stack
                                                                spacing={1}
                                                                direction="row"
                                                            >
                                                                <FiberManualRecordIcon fontSize="small" />
                                                                <Box>
                                                                    <Typography>
                                                                        {
                                                                            el.category
                                                                        }
                                                                    </Typography>
                                                                    {el.predefinedReWorkMessage && (
                                                                        <Stack direction="row">
                                                                            <FiberManualRecordIcon fontSize="small" />
                                                                            <Stack
                                                                                direction="row"
                                                                                spacing={
                                                                                    1
                                                                                }
                                                                            >
                                                                                <Typography className="heading">
                                                                                    Predefined
                                                                                    Reason:
                                                                                </Typography>
                                                                                <Typography>
                                                                                    {
                                                                                        el.predefinedReWorkMessage
                                                                                    }
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Stack>
                                                                    )}
                                                                    {el.statusMessage && (
                                                                        <Stack direction="row">
                                                                            <FiberManualRecordIcon fontSize="small" />
                                                                            <Stack
                                                                                direction="row"
                                                                                spacing={
                                                                                    1
                                                                                }
                                                                            >
                                                                                <Typography className="heading">
                                                                                    Comments:
                                                                                </Typography>
                                                                                <Typography>
                                                                                    {
                                                                                        el.statusMessage
                                                                                    }
                                                                                </Typography>
                                                                            </Stack>
                                                                        </Stack>
                                                                    )}
                                                                </Box>
                                                            </Stack>
                                                        </Box>
                                                    )
                                                )}
                                            </Box>
                                        )}
                                </Stack>
                            </Box>
                        )}
                        <Collapse
                            in={isOpen}
                            timeout="auto"
                            unmountOnExit
                            role="region"
                            id="panel-tablerow"
                            aria-labelledby="action-dropdown-tablerow"
                        >
                            <Table
                                className="table-packets"
                                size="small"
                                aria-label="purchases"
                            >
                                <TableHead className="history-table-head">
                                    <TableRow>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: `${TableCellFirstWidth}px`,
                                            }}
                                        >
                                            <Box>Uploaded</Box>
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: `${TableCellSecondWidth}px`,
                                            }}
                                        >
                                            <Box>Cross Check Type</Box>
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: `${TableCellThirdWidth}px`,
                                            }}
                                        >
                                            <Box>Member ID</Box>
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: `${TableCellFourthWidth}px`,
                                            }}
                                        >
                                            <Box>Escalated age</Box>
                                        </TableCell>
                                        {/* <TableCell align="left">
                                            <Box>CM9 Record No.</Box>
                                        </TableCell> */}
                                        <TableCell
                                            align="left"
                                            sx={{
                                                width: `${TableCellFifthWidth}px`,
                                            }}
                                        >
                                            <Box>Last Updated At</Box>
                                        </TableCell>
                                        <TableCell align="left">
                                            <Box>Archive Till</Box>
                                        </TableCell>
                                        {(_item.packetStatus !== 'Completed' ||
                                            (_item.packetStatus ===
                                                'Completed' &&
                                                _item.enableDocumentDownload ===
                                                    true)) && (
                                            <TableCell align="left">
                                                <Box>Rerun Validation</Box>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow
                                        key={`${_item.packetStatus} - ${index}`}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            data-testid="packetType"
                                        >
                                            <Typography>{`${formattingSingleCalender(
                                                _item.uploadedDate
                                            )} `}</Typography>
                                            <Typography>{`${formattingSingleTime(
                                                _item.uploadedDate
                                            )}`}</Typography>
                                            <Typography>{`(${_item.createdAtDifference} days ago)`}</Typography>
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            data-testid="uploadedDate"
                                        >
                                            {_item.packetType}
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            data-testid="operatorId"
                                        >
                                            {getOperatorName(
                                                _item.operatorName
                                            )}
                                        </TableCell>
                                        {/* Here waiting for escalated data from API */}
                                        <TableCell
                                            align="left"
                                            data-testid="escalatedDate"
                                        >
                                            {_item.packetStatus ===
                                                'Escalated' &&
                                            _item.escalated === true
                                                ? `${formattingDate(
                                                      _item.escalatedDate
                                                  )} (${
                                                      _item.escalatedAtDifference
                                                  } days ago)`
                                                : '-'}
                                        </TableCell>
                                        {/* <TableCell align="left">-</TableCell> */}
                                        <TableCell
                                            align="left"
                                            data-testid="completedDate"
                                        >
                                            <Typography>{`${formattingSingleCalender(
                                                _item.updatedAt
                                            )} `}</Typography>
                                            <Typography>{`${formattingSingleTime(
                                                _item.updatedAt
                                            )}`}</Typography>
                                            <Typography>{`(${_item.updatedAtDifference} days ago)`}</Typography>
                                        </TableCell>
                                        <TableCell
                                            align="left"
                                            data-testid="archiveTill"
                                        >
                                            {_item.packetStatus ===
                                            'Completed' ? (
                                                <>
                                                    <Typography>
                                                        {`${formattingSingleCalender(
                                                            _item.archiveTill
                                                        )}`}
                                                    </Typography>
                                                    <Typography>
                                                        {`${formattingSingleTime(
                                                            _item.archiveTill
                                                        )}`}
                                                    </Typography>
                                                    <Typography>
                                                        {`${
                                                            _item.archiveDateDifference
                                                                ? `(${_item.archiveDateDifference} days ago)`
                                                                : ''
                                                        }`}
                                                    </Typography>
                                                </>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        {(_item.packetStatus !== 'Completed' ||
                                            (_item.packetStatus ===
                                                'Completed' &&
                                                _item.enableDocumentDownload ===
                                                    true)) && (
                                            <TableCell align="left">
                                                {_item.packetStatus ===
                                                    'Categorisation-Completed' ||
                                                _item.packetStatus ===
                                                    'Verifying' ||
                                                _item.packetStatus ===
                                                    'Review' ||
                                                _item.packetStatus ===
                                                    'Generating-Report' ||
                                                _item.packetStatus ===
                                                    'Verified' ||
                                                _item.packetStatus ===
                                                    'Sent-for-Rework' ||
                                                _item.packetStatus ===
                                                    'Validation-Failed' ||
                                                _item.packetStatus ===
                                                    'Completed' ? (
                                                    <IconButton
                                                        edge="end"
                                                        onClick={
                                                            handleForRerunValidation
                                                        }
                                                        className="app-icon-refresh"
                                                        title="rerun packet"
                                                    >
                                                        <RefreshCCW />
                                                    </IconButton>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Collapse>
                    </TableCell>
                </TableRow>
                <TableRow className="divider" />
            </>
        );
    }
);
export default TableRowVirtuoso;
